import { useState } from "react";

import { useSizedImage } from "./imageContext.js";

/** Whether `src` names something to load: not missing, empty or blank. */
export function hasImageSource(src: string | null | undefined): src is string {
  return typeof src === "string" && src.trim() !== "";
}

/**
 * Whether an image can't be drawn, and the `onError` that says so: its source is missing or
 * blank, or it failed to load. The failure belongs to that source, so a new `src` tries again.
 * Every component that draws a remote image takes it, and draws a stand-in or nothing when it is
 * `failed` (`ArtPlaceholder`, `OptionalImage`); `meta/imageFallback.test.ts` holds them to it.
 */
export function useImageFallback(src: string | null | undefined): {
  failed: boolean;
  onError: () => void;
} {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  return {
    failed: !hasImageSource(src) || failedSrc === src,
    onError: () => setFailedSrc(src ?? null),
  };
}

/**
 * `useImageFallback` for a picture drawn at its size (`useSizedImage`): the `src` and `srcSet` to
 * draw, and an `onError` that falls back from a sized copy that fails to the original, so only an
 * original that is missing or fails is `failed`.
 */
export function useSizedImageFallback(
  src: string | null | undefined,
  width?: number,
): { failed: boolean; src: string; srcSet?: string; onError: () => void } {
  const source = src ?? "";
  // The sizer is asked about a real picture only; a missing one is `failed` below.
  const sized = useSizedImage(source, hasImageSource(src) ? width : undefined);
  const fallback = useImageFallback(src);
  // The original a sized copy failed for: drawn in its place, until the picture changes.
  const [unsized, setUnsized] = useState<string | null>(null);
  const original = unsized === source;
  const resized = sized.src !== source || sized.srcSet !== undefined;
  return {
    failed: fallback.failed,
    src: original ? source : sized.src,
    srcSet: original ? undefined : sized.srcSet,
    onError: resized && !original ? () => setUnsized(source) : fallback.onError,
  };
}
